/**
 * Library page — upload tracks, browse by owner, build a shuffle, import
 * from YouTube Music. This is the main landing page.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListPlus, Shuffle, Trash2 } from "lucide-react";
import type { Track } from "@memes/shared";
import {
  useDeleteByOwner,
  useDeleteTrack,
  useOwners,
  useTracks,
} from "../hooks/useTracks";
import { usePutQueue } from "../hooks/useQueue";
import { balancedRecencyShuffle, balancedShuffle } from "../lib/shuffle";
import { fmtDuration, fmtLastPlayed, normalizeLastPlayedSec } from "../lib/format";
import { OwnerBadge } from "../components/OwnerBadge";
import { CoverImg } from "../components/CoverImg";
import { LoudnessIndicator } from "../components/LoudnessIndicator";
import { LibraryUpload } from "../components/LibraryUpload";
import { usePlayer } from "../state/player";
import { YoutubeMusicImportCard } from "../components/YoutubeMusicImportCard";

const DEFAULT_QUEUE_SIZE = 300;

export const Library: React.FC = () => {
  const navigate = useNavigate();
  const { data: tracks = [], isLoading } = useTracks();
  const { data: owners = [] } = useOwners();
  const deleteTrack = useDeleteTrack();
  const deleteByOwner = useDeleteByOwner();
  const putQueue = usePutQueue();
  const startPlayback = usePlayer((s) => s.play);

  const [queueSize, setQueueSize] = useState(DEFAULT_QUEUE_SIZE);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [useRecencyShuffle, setUseRecencyShuffle] = useState(true);

  const normalizedTracks = useMemo<Track[]>(
    () =>
      tracks.map((t) => ({
        ...t,
        lastPlayed: normalizeLastPlayedSec(t.lastPlayed ?? null),
      })),
    [tracks],
  );

  const tracksByOwner = useMemo(() => {
    const map = new Map<string, Track[]>();
    for (const t of normalizedTracks) {
      const arr = map.get(t.owner) ?? [];
      arr.push(t);
      map.set(t.owner, arr);
    }
    return map;
  }, [normalizedTracks]);

  const addTrackToQueue = async (track: Track): Promise<void> => {
    setAddingId(track.id);
    try {
      const { queue, index, currentTime } = usePlayer.getState();
      const newQueue = [...queue, track];
      await putQueue.mutateAsync({
        queue: newQueue.map((t) => ({
          id: t.id,
          durationSec: t.durationSec ?? undefined,
        })),
        currentIndex: index,
        currentTime,
      });
    } finally {
      setAddingId(null);
    }
  };

  const buildShuffle = async (): Promise<void> => {
    const size = Math.max(1, Math.min(queueSize, normalizedTracks.length));
    const ownerList = owners.length > 0 ? owners : [...tracksByOwner.keys()];
    const shuffled = useRecencyShuffle
      ? balancedRecencyShuffle(normalizedTracks, ownerList, 0.5, 2.0)
      : balancedShuffle(normalizedTracks, ownerList);
    const picked = shuffled.slice(0, size);
    await putQueue.mutateAsync({
      queue: picked.map((t) => ({ id: t.id, durationSec: t.durationSec ?? undefined })),
      currentIndex: 0,
      currentTime: 0,
    });
    startPlayback(0);
    navigate("/queue");
  };

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Library</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {normalizedTracks.length} tracks across {tracksByOwner.size} owners
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={useRecencyShuffle}
              onChange={(e) => setUseRecencyShuffle(e.target.checked)}
            />
            Recency-weighted
          </label>
          <input
            type="number"
            value={queueSize}
            min={1}
            max={normalizedTracks.length || 1}
            onChange={(e) => setQueueSize(Math.max(1, Number(e.target.value)))}
            className="w-20 rounded-xl border px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-700"
          />
          <button
            className="btn btn-primary"
            onClick={buildShuffle}
            disabled={normalizedTracks.length === 0}
          >
            <Shuffle className="h-4 w-4" /> Shuffle &amp; play
          </button>
        </div>
      </header>

      <LibraryUpload owners={owners} />

      <YoutubeMusicImportCard owners={owners} />

      {isLoading && <p className="text-sm">Loading tracks…</p>}

      {[...tracksByOwner.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([owner, list]) => (
          <section key={owner} className="card p-4">
            <div className="mb-3 flex items-center justify-between gap-4">
              <h3 className="flex items-center gap-2 text-base font-semibold">
                <OwnerBadge owner={owner} />
                <span className="text-gray-500">{list.length} tracks</span>
              </h3>
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (confirm(`Delete all tracks by ${owner}?`)) {
                    void deleteByOwner.mutateAsync(owner);
                  }
                }}
              >
                <Trash2 className="h-4 w-4" /> Delete {owner}
              </button>
            </div>
            <ul className="divide-y dark:divide-gray-800">
              {[...list]
                .sort((a, b) => {
                  const ap = a.lastPlayed ?? 0;
                  const bp = b.lastPlayed ?? 0;
                  if (ap === 0 && bp === 0) return 0;
                  if (ap === 0) return -1;
                  if (bp === 0) return 1;
                  return ap - bp;
                })
                .map((t) => (
                <li key={t.id} className="flex items-center gap-3 py-2">
                  <CoverImg track={t} className="h-10 w-10" rounded="rounded-md" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {t.metadataTitle ?? t.name}
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      {t.metadataArtist ?? "Unknown artist"} ·{" "}
                      {fmtDuration(t.durationSec)} ·{" "}
                      <span
                        className={
                          t.lastPlayed == null
                            ? "text-indigo-600 dark:text-indigo-400"
                            : undefined
                        }
                        title={
                          t.lastPlayed != null
                            ? new Date(t.lastPlayed * 1000).toLocaleString()
                            : undefined
                        }
                      >
                        {fmtLastPlayed(t.lastPlayed)}
                      </span>
                    </div>
                  </div>
                  <LoudnessIndicator track={t} />
                  <button
                    type="button"
                    className="btn"
                    title="Add to end of queue"
                    disabled={addingId === t.id}
                    onClick={() => void addTrackToQueue(t)}
                  >
                    <ListPlus className="h-4 w-4" />
                  </button>
                  <button
                    className="btn btn-danger"
                    aria-label={`Delete ${t.name}`}
                    onClick={() => {
                      if (confirm(`Delete "${t.metadataTitle ?? t.name}"?`))
                        void deleteTrack.mutateAsync(t.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
    </div>
  );
};
